import React, {
  ChangeEvent,
  useMemo,
  useState,
  useEffect,
  useDeferredValue,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import dynamic from 'next/dynamic';
import { setError } from '../../actions';
import {
  fallbackStyles,
  ResidenceControlsWrapper,
  SearchPlaceholder,
  Spinner,
  SpinnerWrapper
} from './styled';
import { AgencyCardsSkeleton } from '../Cards/AgencyCard/AgencyCardSkeleton';
import { BusinessItemsListPageProps, BusinessItemsType, Item } from './types';
import {
  BusinessItemsList,
  EndlessBusinessItemsList
} from './BusinessItemsList';
import { getFilteredValue } from '../../utils/helpers/getFilteredValue';
import { PageWrapper } from '../../styled/pageLayout.styles';
import { AdaptivePageHeader } from '../ui/PageHeader/AdaptivePageHeader';
import { AgencyCard } from '../Cards/AgencyCard';
import { ResidenceCard } from '../Cards/ResidencesCard';

const NewInput = dynamic(
  () => import('../ui/Input/NewInput').then(mod => mod.NewInput),
  {
    ssr: false,
    loading: SearchPlaceholder
  }
);

const FallbackView = dynamic(
  () => import('../FallbackView').then(mod => mod.FallbackView),
  { ssr: false }
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const BusinessItemsListPageWrapperComponent: React.FC<
  BusinessItemsListPageProps
> = ({
  setErrorUI,
  title,
  hasError,
  items,
  searchItems,
  ListContainer,
  type = BusinessItemsType.agencies,
  children,
  loading = false,
  isMapView = false,
  mapView,
  onLoadMore,
  isStopLoad = true,
  isLoadingMore = false,
  onSearchQuery
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const deferredClientSearch = useDeferredValue(searchValue);

  const filteredItems = useMemo(() => {
    if (onSearchQuery || !deferredClientSearch.trim()) {
      return items;
    }

    return getFilteredValue(searchItems ?? items, deferredClientSearch);
  }, [deferredClientSearch, items, onSearchQuery, searchItems]);

  const isServerSearch = !!onSearchQuery;
  const isSearchActive = isServerSearch
    ? !!searchValue.trim()
    : !!deferredClientSearch.trim();

  const isResidencePage = type === BusinessItemsType.residences;
  const CardComponent = (
    isResidencePage ? ResidenceCard : AgencyCard
  ) as React.ComponentType<Item>;
  const inputPlaceholder = t(
    isResidencePage ? 'residences.search' : 'pages.agencies.search'
  );

  useEffect(() => {
    onSearchQuery?.(searchValue.trim());
  }, [searchValue, onSearchQuery]);

  const itemList = useMemo(() => {
    if (isServerSearch && loading) {
      return <AgencyCardsSkeleton />;
    }

    if (filteredItems?.length) {
      return filteredItems.map(item => (
        <CardComponent key={item.id} {...item} />
      ));
    }

    return (
      <FallbackView
        width={213}
        height={155}
        text={t('pages.business_pages.no_results')}
        icon="/static/images/new_ad/icons/icon-not-found.png"
        styles={fallbackStyles}
      />
    );
  }, [CardComponent, filteredItems, isServerSearch, loading, t]);

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const onResetHandler = () => setSearchValue('');

  useEffect(() => {
    if (hasError) setErrorUI(true);
  }, [hasError, setErrorUI]);

  const isEmptyView = !filteredItems?.length && !loading;

  const listProps = {
    ListContainer,
    isEmptyView,
    type,
    children: itemList
  };

  const isBlockingLoad = loading && !isServerSearch;
  const isSearchLoading = loading && isServerSearch;
  const showList = !isMapView && (!loading || isSearchLoading);

  const headerContent = (
    <>
      {!isMapView && (
        <NewInput
          stat={`search-${isResidencePage ? 'complex' : 'agency'}`}
          name={`${type}-page-search`}
          placeholder={inputPlaceholder}
          autoComplete="on"
          onChangeHandler={onChangeHandler}
          value={searchValue}
          isShowSearch
          isShowReset={!!searchValue}
          onResetHandler={onResetHandler}
        />
      )}
      {children}
    </>
  );

  return (
    <PageWrapper>
      <AdaptivePageHeader
        title={title}
        type={type}
        isResidencePage={isResidencePage}
      >
        {isResidencePage ? (
          <ResidenceControlsWrapper>{headerContent}</ResidenceControlsWrapper>
        ) : (
          headerContent
        )}
      </AdaptivePageHeader>
      {isBlockingLoad && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      {showList &&
        (onLoadMore && (!isSearchActive || onSearchQuery) ? (
          <EndlessBusinessItemsList
            ref={listRef}
            items={filteredItems}
            loading={isLoadingMore}
            isStopLoad={isStopLoad}
            onLoadMore={onLoadMore}
            {...listProps}
          />
        ) : (
          <BusinessItemsList {...listProps} />
        ))}
      {isMapView && mapView}
    </PageWrapper>
  );
};

export const BusinessItemsListPageWrapper = connect(
  null,
  mapDispatchToProps
)(React.memo(BusinessItemsListPageWrapperComponent));
